import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Card, Tabs, Typography, Space, Tooltip, Divider, Row, Col, Grid } from "antd";
import { t } from "i18next";
import {
    EditOutlined,
    DeleteOutlined,
    UndoOutlined,
    WalletOutlined,
    FileTextOutlined,
    PrinterOutlined,
    MoreOutlined,
    BarcodeOutlined,
    ApiOutlined,
    SendOutlined,
    CopyOutlined,
    CheckOutlined,
    DownOutlined
} from '@ant-design/icons';
import { MdOutlineMedicalServices } from "react-icons/md";
import moment from "moment";
import Helper from "../../../core/Helper";

// Context & Services
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import ApiService from "../../../core/service/ApiService";
import { useModal } from "../../../core/context/ModalContext";

// Components
import AppointmentPreview from "./AppointmentPreview";
import OperationsPreview from "./OperationsPreview";
import PriceSummaryPreview from "./PriceSummaryPreview";
import PaymentsPreview from "./PaymentsPreview";
import LogsPreview from "./LogsPreview";
import StateChanger from "../StateChanger";
import FormDownload from "../FormDownload";
import BarkodePrinter from "../BarcodePrinter";
import AppointmentFiles from "../files/AppointmentFiles";
import Hl7MessagesPreview from "./Hl7MessagesPreview";
import { needsConsumableConfirm, useConsumableConfirm } from "../../consumable/ConsumableConfirmModal";
import { Alert, Dropdown, Select, Switch, message } from "antd";
import * as qz from "qz-tray";
import { isMobile } from "react-device-detect";
import { navigate } from "../../../core/service/Navigation";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export default function AppointmentPreviewApp({ appointmentId, onDelete }) {
    const screens = Grid.useBreakpoint();
    const history = useHistory();
    const { hasPermission } = useUser();
    const [activeTab, setActiveTab] = useState('appointment');
    const [barcodePdf, setBarcodePdf] = useState('');
    const [sendEnabizLoading, setSendEnabizLoading] = useState(false);
    const [copiedAll, setCopiedAll] = useState(false);
    const { user } = useUser();
    const wrapperRef = useRef(null);

    const [inModal, setInModal] = useState(false);

    // Bu component'i içeren modal-body'nin padding'ini kaldır (sadece bu modal'ı etkiler).
    // Modalın "Randevu Önizleme" başlığı da gizlenir: yerini "Randevu #id · Tümünü Kopyala" satırı alır
    // (çağıran yerlerin başlık metnine dokunmadan).
    useEffect(() => {
        const modalBody = wrapperRef.current?.closest('.ant-modal-body');
        if (!modalBody) return;
        const originalPadding = modalBody.style.padding;
        modalBody.style.padding = '0';

        const modalHeader = modalBody.parentElement?.querySelector(':scope > .ant-modal-header');
        const originalHeaderDisplay = modalHeader?.style.display;
        if (modalHeader) modalHeader.style.display = 'none';
        setInModal(true);

        return () => {
            modalBody.style.padding = originalPadding;
            if (modalHeader) modalHeader.style.display = originalHeaderDisplay;
        };
    }, []);

    // modal-content'in kendi dolgusu (antd: 20px 24px) da kaldırılır: iç içe dört ayrı sol boşluk
    // oluşuyordu (modal + sarmalayıcı + kart listesi + kart). Tek kenar boşluğu sarmalayıcının 12px'i.
    useEffect(() => {
        const modalContent = wrapperRef.current?.closest('.ant-modal-content');
        if (!modalContent) return;
        const originalPadding = modalContent.style.padding;
        modalContent.style.padding = '0';
        return () => { modalContent.style.padding = originalPadding; };
    }, []);

    // State
    const [appointment, setAppointment] = useState({
        id: null,
        patientId: '',
        patient: {},
        unit: {},
        doctor: {},
        institution: {},
        appointmentOperations: [],
        appointmentOtherOperations: [],
        state: {} // state.id, state.color, state.name
    });
    const [payments, setPayments] = useState([]);
    const [logs, setLogs] = useState([]);

    // --- API CALLS ---
    const getAppointment = async () => {
        if (appointmentId) {
            const res = await ApiService.Appointment.GetAppointment(appointmentId);
            if (res.success) setAppointment(res.data);
        }
    }

    const getPayments = async () => {
        if (appointmentId) {
            const res = await ApiService.Payment.GetPaymentList(appointmentId, true);
            if (res.success) setPayments(res.data);
        }
    }

    // Log sekmesi APPLOG_VIEW yetkisine bağlı; yetkisi olmayan kullanıcı için istek de atılmamalı.
    const getLogs = async () => {
        if (appointmentId && hasPermission(PERMISSIONS.APPLOG_VIEW)) {
            const res = await ApiService.AppLog.GetAppLogsByAppointmentId(appointmentId);
            if (res.success) setLogs(res.data);
        }
    }

    const onAppointmentId = async () => {
        await getAppointment();
        await getPayments();
        await getLogs();
    }

    useEffect(() => {
        onAppointmentId();
    }, [appointmentId]);

    // E-Nabız otomatik gönderimi yalnızca Kurum + Birim + Randevu Tipi üçü de aktifse çalışır.
    // (AppointmentDetailEdit'teki enabizConfigEnabled ile aynı kural.) Üçünden biri pasifse
    // elle gönderim yine mümkün ama kullanıcı uyarılıyor.
    const enabizConfigEnabled =
        appointment?.unit?.enabizEnabled === true &&
        appointment?.institution?.enabizEnabled === true &&
        appointment?.appointmentType?.enabizEnabled === true;

    // --- ACTIONS ---
    // Önizleme çoğunlukla modalda (Router dışında) açılır: navigate() tam sayfa yüklemeden geçer,
    // modal sayfa değişince NavigationBridge tarafından kapatılır.
    const goPayments = () => {
        navigate(`/app/patients/${appointment.patientId}/appointments/${appointmentId}/payments`);
    }

    const openPatientResults = async () => {
        const res = await ApiService.Patient.GetToken(appointment.patientId);
        if (res.success) {
            const url = `${window.AppSettings.server.result}?access_token=${res.data}`;
            window.open(url, '_blank');
        }
    }

    const deleteAppointment = async () => {
        const res = await ApiService.Appointment.DeleteAppointment(appointmentId);
        if (res.success) onDelete && onDelete();
    }

    const restoreAppointment = async () => {
        const res = await ApiService.Appointment.RestoreAppointment(appointmentId);
        if (res.success) onDelete && onDelete();
    }

    const editAppointment = () => {
        navigate(`/app/patients/${appointment.patientId}/appointments/${appointmentId}`);
    }

    const [askConsumableConfirm, consumableConfirmModal] = useConsumableConfirm();

    // Tamamlanmamış randevu "Tamamlandı" yapılırken sarf onayı (stok bu kayıtla düşer).
    const beforeStateChange = async (state) => {
        if (state?.stateGroup?.id !== 2 || appointment?.state?.stateGroup?.id === 2)
            return true;
        const res = await ApiService.AppointmentConsumable.GetAppointmentConsumables(appointmentId);
        if (!res.success)
            return false;
        const pending = (res.data || [])
            .filter(x => !x.confirmedDate)
            .map(x => ({ ...x, key: `s:${x.id}` }));
        if (!needsConsumableConfirm(pending))
            return true;
        return await askConsumableConfirm(pending);
    }

    const onStateChange = async (stateId, stateDescForm, state, consumableConfirmations) => {
        const res = await ApiService.Appointment.EditAppointmentState(appointmentId, {
            stateId,
            stateDescForm,
            consumableConfirmations: Array.isArray(consumableConfirmations) ? consumableConfirmations : null
        });
        if (res.success) await getAppointment();
    }

    const [checkInLoading, setCheckInLoading] = useState(false);
    const toggleCheckIn = async (checked) => {
        setCheckInLoading(true);
        try {
            const res = await ApiService.Appointment.CheckIn(appointmentId, { checkIn: checked });
            if (res.success) {
                await getAppointment();
            }
        } finally {
            setCheckInLoading(false);
        }
    }

    const printBarcode = async (e) => {
        e.stopPropagation();
        const res = await ApiService.Barcode.GetBarcode(appointmentId);
        if (res.success) {
            setBarcodePdf(res.data);
        } else {
            message.error(t('Could not get the PDF'));
        }
    };

    const buildAppointmentText = () => {
        const lines = [];
        const sep = '═══════════════════════════════════════';
        const subSep = '───────────────────────────────────────';
        const pad = (label) => label.padEnd(13, ' ');

        lines.push(sep);
        lines.push(t('APPOINTMENT INFORMATION'));
        lines.push(sep);
        lines.push('');

        // HASTA BİLGİLERİ
        const p = appointment.patient || {};
        const patientLines = [];
        const fullName = `${p.name || ''} ${p.lastname || ''}`.trim();
        if (fullName) patientLines.push(`${pad('Ad Soyad')}: ${fullName}`);
        if (p.customId) {
            const idStr = p.otherPatientId ? `${p.customId} (Diğer ID: ${p.otherPatientId})` : p.customId;
            patientLines.push(`${pad('Hasta ID')}: ${idStr}`);
        }
        if (p.gender) patientLines.push(`${pad('Cinsiyet')}: ${t(p.gender)}`);
        if (p.birthDay && p.birthMonth && p.birthYear) {
            const bd = `${String(p.birthDay).padStart(2, '0')}/${String(p.birthMonth).padStart(2, '0')}/${p.birthYear}`;
            patientLines.push(`${pad(t('Birthdate'))}: ${bd}`);
        }
        if (p.motherName || p.fatherName) {
            patientLines.push(`${pad('Anne / Baba')}: ${p.motherName || '-'} / ${p.fatherName || '-'}`);
        }
        if (p.phone1) patientLines.push(`${pad('Telefon')}: ${Helper.Phone.intlPhoneFormat(p.phoneCode1, p.phone1)}`);
        if (p.phone2) patientLines.push(`${pad('Telefon 2')}: ${Helper.Phone.intlPhoneFormat(p.phoneCode2, p.phone2)}`);
        if (p.email) patientLines.push(`${pad('E-posta')}: ${p.email}`);
        if (p.note) patientLines.push(`${pad('Hasta Notu')}: ${p.note}`);

        if (patientLines.length) {
            lines.push('▸ ' + t('PATIENT INFORMATION'));
            lines.push(subSep);
            lines.push(...patientLines);
            lines.push('');
        }

        // RANDEVU DETAYLARI
        const detailLines = [];
        if (appointment.institution?.name) detailLines.push(`${pad('Kurum')}: ${appointment.institution.name}`);
        if (appointment.unit?.name) detailLines.push(`${pad('Birim')}: ${appointment.unit.name}`);
        if (appointment.doctor?.name) detailLines.push(`${pad('Doktor')}: ${appointment.doctor.name}`);
        if (appointment.appointmentType?.name) detailLines.push(`${pad('Randevu Tipi')}: ${appointment.appointmentType.name}`);
        if (appointment.startDate) {
            const date = moment(appointment.startDate).format('DD/MM/YYYY');
            const day = moment(appointment.startDate).format('dddd');
            detailLines.push(`${pad('Tarih')}: ${date} (${t(day)})`);
            const startTime = moment(appointment.startDate).format('HH:mm');
            const endTime = appointment.endDate ? moment(appointment.endDate).format('HH:mm') : '';
            detailLines.push(`${pad('Saat')}: ${startTime}${endTime ? ' - ' + endTime : ''}`);
        }
        if (appointment.state?.name) detailLines.push(`${pad('Durum')}: ${appointment.state.name}`);
        if (appointment.checkIn && appointment.checkInDate) {
            detailLines.push(`${pad('Check-in')}: ${moment(appointment.checkInDate).format('HH:mm')}`);
        }
        if (appointment.anamnez) detailLines.push(`${pad('Anamnez')}: ${appointment.anamnez}`);
        if (appointment.patientComplaintHistory) detailLines.push(`${pad('Şik. Geçmişi')}: ${appointment.patientComplaintHistory}`);
        if (appointment.note) detailLines.push(`${pad('Not')}: ${appointment.note}`);

        if (detailLines.length) {
            lines.push('▸ RANDEVU DETAYLARI');
            lines.push(subSep);
            lines.push(...detailLines);
            lines.push('');
        }

        // İŞLEMLER
        const ops = appointment.appointmentOperations || [];
        const otherOps = appointment.appointmentOtherOperations || [];
        if (ops.length || otherOps.length) {
            lines.push('▸ ' + t('OPERATIONS'));
            lines.push(subSep);
            let idx = 1;
            ops.forEach((op) => {
                const name = op.operation?.name || '-';
                const acc = op.accessionNumber ? ` [ACC: ${op.accessionNumber}]` : '';
                lines.push(`${idx}. ${name}${acc}`);
                idx++;
            });
            otherOps.forEach((op) => {
                lines.push(`${idx}. ${op.name || '-'} (diğer)`);
                idx++;
            });
            lines.push('');
        }

        // İŞLEM BİLGİLERİ
        lines.push('▸ ' + t('OPERATION INFORMATION'));
        lines.push(subSep);
        lines.push(`${pad('Randevu ID')}: ${appointment.id || appointmentId}`);
        lines.push(sep);

        return lines.join('\n');
    };

    const copyAllInfo = async () => {
        try {
            const text = buildAppointmentText();
            await navigator.clipboard.writeText(text);
            setCopiedAll(true);
            setTimeout(() => setCopiedAll(false), 2000);
        } catch (err) {
            message.error(t('Copy failed') + ' ❌');
        }
    };

    const sendBarcodeToSelectedPrinter = async () => {
        try {
            if (!qz.websocket.isActive()) {
                await qz.websocket.connect({ verify: false });
            }

            const res = await ApiService.Barcode.GetBarcode(appointmentId);
            if (!res.success || !res.data) {
                message.error(t('Could not get the PDF'));
                return;
            }

            const pdfUrl = res.data;
            const pdfResponse = await fetch(pdfUrl);
            const pdfBlob = await pdfResponse.blob();
            const pdfArrayBuffer = await pdfBlob.arrayBuffer();
            const pdfBase64 = btoa(String.fromCharCode(...new Uint8Array(pdfArrayBuffer)));
            if (!user.printerName) {
                message.warning(t('You must select a printer on the user page!'));
                return;
            }
            const config = qz.configs.create(user.printerName);
            const data = [{ type: 'pdf', format: 'base64', data: pdfBase64 }];

            await qz.print(config, data);

        } catch (err) {
            message.error(t('Printing failed'));
            console.log(err);
        }
    };

    // Durum + check-in: randevu kartında (AppointmentPreview) başlığın sağında gösterilir.
    // Mobilde global kural seçici metnini 16px yapıyor; small seçiciye sığmayıp taşıyordu →
    // mobilde normal boy; Check-in'in yanında kalan yeri doldurur (alt satıra itmez).
    const narrow = !screens.md
    const stateControls = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <StateChanger
                size={narrow ? 'middle' : 'small'}
                style={narrow ? { width: 'auto', flex: '1 1 0', minWidth: 0 } : { width: 190 }}
                onStateChange={onStateChange}
                beforeStateChange={beforeStateChange}
                appointmentId={appointmentId}
                selectedStateId={appointment.state.id}
                checkIn={appointment.checkIn}
            />

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <Switch
                    size={narrow ? 'default' : 'small'}
                    loading={checkInLoading}
                    checked={!!appointment.checkIn}
                    onChange={toggleCheckIn}
                />
                <Text style={{ fontSize: 13 }}>
                    {t('Check-in')}
                    {/* Check-in saatinin gösterildiği tek yer */}
                    {appointment.checkIn && appointment.checkInDate && (
                        <Text type="secondary" style={{ fontSize: 13 }}> {moment(appointment.checkInDate).format('HH:mm')}</Text>
                    )}
                </Text>
            </label>
        </div>
    );

    // Barkod menüsü: yalnızca yetkisi olunan işlemler; hiçbiri yoksa buton gizlenir
    const barcodeMenuItems = [
        hasPermission(PERMISSIONS.APPOINTMENT_PRINTER_BARCODE) && { key: 'print', icon: <PrinterOutlined />, label: t('Print Barcode') },
        hasPermission(PERMISSIONS.APPOINTMENT_SEND_BARCODE) && { key: 'send', icon: <SendOutlined />, label: t('Send Barcode') },
    ].filter(Boolean);

    return (
        <div ref={wrapperRef} style={{ minHeight: '100vh', backgroundColor: '#f5f7fa', paddingBottom: 40 }}>
            {consumableConfirmModal}

            {/* --- HEADER (ACTION BAR) ---
                Üst satır: kimlik + Tümünü Kopyala. Durum/check-in randevu kartında.
                Alt satır: yardımcı işlemler solda; Ödeme, Düzenle ve silme sağda.
                Durum yalnızca seçicide gösterilir (ayrı Tag tekrarıydı); tüm kontroller small. */}
            <div style={{
                backgroundColor: '#fff',
                padding: '12px 12px 10px',
                borderBottom: '1px solid #e8e8e8',
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
            }}>
                {/* Başlık satırı (modalda modal başlığının yerinde; sağda kapatma düğmesine yer bırakılır). */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingRight: inModal ? 32 : 0 }}>
                    <Title level={5} style={{ margin: 0, whiteSpace: 'nowrap' }}>
                        {t('Appointment')} #{appointmentId}
                    </Title>
                    <Button
                        size={'small'}
                        icon={copiedAll ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CopyOutlined />}
                        onClick={copyAllInfo}
                        title={copiedAll ? t('Copied') : t('Copy All')}
                        aria-label={t('Copy All')}
                    />
                </div>

                {/* İşlem satırı: yardımcı işlemler solda; Ödeme, Düzenle ve silme sağda. */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: narrow ? 'nowrap' : 'wrap', gap: 8 }}>
                    <Space size={'small'} wrap={!narrow}>
                        {/* Mobilde barkod ve onam formu gizli: işlem satırı tek satıra sığsın */}
                        {!narrow && barcodeMenuItems.length > 0 && (
                            <Dropdown
                                trigger={['click']}
                                menu={{ items: barcodeMenuItems, onClick: ({ key, domEvent }) => key === 'print' ? printBarcode(domEvent) : sendBarcodeToSelectedPrinter() }}
                            >
                                <Button size={'small'} icon={<BarcodeOutlined />}>
                                    {t('Barcode')} <DownOutlined style={{ fontSize: 10 }} />
                                </Button>
                            </Dropdown>
                        )}

                        {/* Mobilde yalnız ikon: 360px ekranda işlem satırı tek satıra sığsın */}
                        <Button
                            size={'small'}
                            icon={<FileTextOutlined />}
                            onClick={openPatientResults}
                            title={narrow ? t('Patient Results') : undefined}
                            aria-label={t('Patient Results')}
                        >
                            {narrow ? null : t('Patient Results')}
                        </Button>

                        {!narrow && <FormDownload appointmentId={appointmentId} unitId={appointment?.unit?.id} />}
                    </Space>

                    <Space size={'small'}>
                        {hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT) && (
                            <Button size={'small'} icon={<WalletOutlined />} onClick={goPayments}>
                                {t('Pay')}
                            </Button>
                        )}
                        {hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT) && (
                            <Button size={'small'} type="primary" icon={<EditOutlined />} onClick={editAppointment}>
                                {t('Edit')}
                            </Button>
                        )}
                        {hasPermission(PERMISSIONS.APPOINTMENT_DELETE) && (
                            <Tooltip title={!appointment?.isDeleted ? t('Delete') : t('Restore')}>
                                <Button
                                    size={'small'}
                                    type="text"
                                    danger
                                    icon={!appointment?.isDeleted ? <DeleteOutlined /> : <UndoOutlined />}
                                    onClick={!appointment?.isDeleted ? deleteAppointment : restoreAppointment}
                                />
                            </Tooltip>
                        )}
                    </Space>
                </div>

                {/* Gizli Yazdırma Frame'i */}
                <iframe
                    id="print-pdf-barkode"
                    src={barcodePdf}
                    style={{ display: 'none', width: 0, height: 0, position: 'absolute' }}
                    title="barcode-printer"
                    onLoad={(e) => {
                        if (barcodePdf) {
                            const frameWindow = e.target.contentWindow;
                            if (frameWindow) {
                                frameWindow.focus();
                                frameWindow.print();
                            }
                        }
                    }}
                />
            </div>

            {/* --- CONTENT --- */}
            <div style={{ padding: '0 12px' }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    type="card"
                    size="large"
                    style={{ marginBottom: 32 }}
                    className="modern-tabs"
                >
                    {/* --- TAB 1: RANDEVU & İŞLEMLER --- */}
                    <TabPane tab={<span><MdOutlineMedicalServices style={{ marginRight: 8, position: 'relative', top: 2 }} />{t('Appointment')}</span>} key="appointment">

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            {/* 1. HASTA VE RANDEVU BİLGİLERİ (BAĞLAM) - Nötr/Gri Ton */}
                            <AppointmentPreview appointment={appointment} patient={appointment.patient} stateControls={stateControls} />

                            {/* 2. İŞLEMLER — kart içinde kart yok: masaüstünde başlık tablonun içinde,
                                mobilde bölüm başlığı + her işlem ayrı kart. E-Nabız gönder başlığın sağında. */}
                            <OperationsPreview
                                title={t('Operations')}
                                extra={appointment?.state?.stateGroup?.id === 2 && hasPermission(PERMISSIONS.ENABIZ) && (
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<SendOutlined />}
                                        loading={sendEnabizLoading}
                                        onClick={async () => {
                                            setSendEnabizLoading(true);
                                            try {
                                                const res = await ApiService.AppointmentTeletip.SendEnabizBatch(appointmentId);
                                                if (res.success) {
                                                    message.success(t('E-Nabız sent successfully. Count: ') + res.data);
                                                    await getAppointment();
                                                }
                                            } finally {
                                                setSendEnabizLoading(false);
                                            }
                                        }}
                                    >
                                        {t('Send E-Nabız')}
                                    </Button>
                                )}
                                notice={appointment?.state?.stateGroup?.id === 2 && hasPermission(PERMISSIONS.ENABIZ) && !enabizConfigEnabled && (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        message={t('E-Nabız sending appears to be disabled for the institution / unit / appointment type. Please inform IT before sending.')}
                                    />
                                )}
                                operations={appointment.appointmentOperations.map(x => ({ ...x, appointmentId, startDate: appointment.startDate, currency: appointment.currency }))}
                                otherOperations={appointment.appointmentOtherOperations.map(x => ({ ...x, appointmentId, startDate: appointment.startDate, currency: appointment.currency }))}
                            />

                            {/* 3. FİNANSAL BİLGİLER — ödeme modalı gibi nötr; renk yalnızca tutarlarda (borç / fark) */}
                            {hasPermission(PERMISSIONS.PAYMENT_VIEW) && (
                                <Card
                                    title={t('Financial Information')}
                                    bordered={false}
                                    style={{ borderRadius: 8 }}
                                    headStyle={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}
                                >
                                    {/* Üstte özet şeridi, altında tam genişlik ödeme geçmişi */}
                                    <PriceSummaryPreview
                                        totalPrice={appointment.totalPrice}
                                        totalPaid={appointment.totalPaid}
                                        currency={appointment.currency}
                                    />
                                    <Title level={5} style={{ margin: '20px 0 8px', fontSize: 14 }}>{t('Payment History')}</Title>
                                    <PaymentsPreview payments={payments} appointmentCurrency={appointment.currency} />
                                </Card>
                            )}
                        </div>
                    </TabPane>

                    {/* --- TAB 3: DOSYALAR --- */}
                    {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && (
                        <TabPane tab={<span><FileTextOutlined /> {t('Files')}</span>} key="files">
                            <Card title={t('Attached Files')} bordered={false} style={{ borderRadius: 8 }}>
                                <AppointmentFiles appointmentId={appointment.id} />
                            </Card>
                        </TabPane>
                    )}

                    {/* --- TAB 4: HL7 MESAJLARI --- */}
                    {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && (
                        <TabPane tab={<span><ApiOutlined /> {t('HL7 Messages')}</span>} key="hl7messages">
                            <Card title={t('HL7 Messages')} bordered={false} style={{ borderRadius: 8 }}>
                                <Hl7MessagesPreview appointmentId={appointment.id} />
                            </Card>
                        </TabPane>
                    )}

                    {/* --- TAB 5: LOGLAR --- */}
                    {hasPermission(PERMISSIONS.APPLOG_VIEW) && (
                        <TabPane tab={<span><MoreOutlined /> {t('Logs')}</span>} key="logs">
                            <Card title={t('Activity Logs')} bordered={false} style={{ borderRadius: 8 }} bodyStyle={isMobile ? { padding: '4px 12px' } : undefined}>
                                <LogsPreview logs={logs} />
                            </Card>
                        </TabPane>
                    )}
                </Tabs>
            </div>
        </div>
    );
}